import AutoSizedTextArea from 'components/inputs/AutoSizedTextArea';
import LabelWrap from 'components/inputs/LabelWrap';
import Select from 'components/inputs/Select';
import Modal, { modalContentClass } from 'components/Modal';
import { BookedToChangeReasonEnum } from 'api/model';
import {
  BOOKED_TO_CHANGE_REASON_GROUPS,
  bookedToChangeReasonRequiresComment,
} from 'constants/BookedToChangeReasons';
import { FC, useState } from 'react';
import styled from 'styled-components';
import { formatDateTime } from 'utils/date-helpers';

const StyledModal = styled(Modal)`
  .${modalContentClass} {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 350px;

    select,
    textarea {
      width: auto;
    }
  }
`;

const Shift = styled.div`
  font-variant-numeric: tabular-nums;
`;

/** "3 h 30 min senare", "1 d tidigare". */
const describeShift = (from: Date, to: Date) => {
  const minutes = Math.round((to.getTime() - from.getTime()) / 60000);
  const total = Math.abs(minutes);
  const days = Math.floor(total / (24 * 60));
  const hours = Math.floor((total % (24 * 60)) / 60);
  const mins = total % 60;
  const parts = [
    days > 0 ? `${days} d` : null,
    hours > 0 ? `${hours} h` : null,
    mins > 0 && days === 0 ? `${mins} min` : null,
  ].filter(Boolean);
  return `${parts.join(' ')} ${minutes > 0 ? 'senare' : 'tidigare'}`;
};

export interface BookedToChange {
  reason: BookedToChangeReasonEnum;
  comment?: string;
}

interface Props {
  from: Date;
  to: Date;
  onConfirm(change: BookedToChange): void;
  onClose(): void;
}

/**
 * Frågar efter orsaken när Bokad till flyttas direkt i uppdragslistan. Ledningsrapporten räknar
 * ombokningar per orsak, så tiden sparas först när orsaken är vald.
 */
const BookedToChangeReasonModal: FC<Props> = ({
  from,
  to,
  onConfirm,
  onClose,
}) => {
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');

  const handleConfirm = () => {
    if (!reason) {
      setError('Välj orsak till ny bokad tid');
      return;
    }
    if (bookedToChangeReasonRequiresComment(reason) && !comment.trim()) {
      setError('Skriv en kommentar när orsaken är Annat');
      return;
    }
    onConfirm({
      reason: Number(reason) as BookedToChangeReasonEnum,
      comment: comment.trim() || undefined,
    });
  };

  return (
    <StyledModal
      buttons={[
        { label: 'Spara tid', onClick: handleConfirm },
        { label: 'Ångra', onClick: onClose },
      ]}
      onClose={onClose}
      title="Varför flyttas tiden?"
    >
      <Shift>
        {formatDateTime(from)} → {formatDateTime(to)} ·{' '}
        {describeShift(from, to)}
      </Shift>
      <LabelWrap
        label="Orsak"
        errorLabel={!reason && error ? error : undefined}
      >
        <Select
          autoFocus
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            setError('');
          }}
        >
          <option value="">Välj orsak...</option>
          {BOOKED_TO_CHANGE_REASON_GROUPS.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.reasons.map((r) => (
                <option key={r.value} value={String(r.value)}>
                  {r.label}
                </option>
              ))}
            </optgroup>
          ))}
        </Select>
      </LabelWrap>
      <LabelWrap
        label={
          bookedToChangeReasonRequiresComment(reason)
            ? 'Kommentar'
            : 'Kommentar (valfri)'
        }
        errorLabel={reason && error ? error : undefined}
      >
        <AutoSizedTextArea
          value={comment}
          onChange={(e) => {
            setComment((e.target as HTMLTextAreaElement).value);
            setError('');
          }}
          placeholder='t.ex. "Kunden jobbar till 16"'
        />
      </LabelWrap>
    </StyledModal>
  );
};

export default BookedToChangeReasonModal;
